import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";

const ModalManagerContext = createContext(null);

export const useModalManager = () => {
  const context = useContext(ModalManagerContext);
  if (!context) {
    throw new Error("useModalManager must be used within ModalManagerProvider");
  }
  return context;
};

export const ModalManagerProvider = ({ children }) => {
  const [activeModalId, setActiveModalId] = useState(null);

  const openModal = useCallback((modalId) => {
    setActiveModalId(modalId);
  }, []);

  const closeModal = useCallback((modalId) => {
    setActiveModalId((current) => (current === modalId ? null : current));
  }, []);

  const value = useMemo(
    () => ({ activeModalId, openModal, closeModal }),
    [activeModalId, openModal, closeModal],
  );

  return (
    <ModalManagerContext.Provider value={value}>
      {children}
    </ModalManagerContext.Provider>
  );
};

export const useManagedModal = (modalId, show, handleClose) => {
  const { activeModalId, openModal, closeModal } = useModalManager();

  useEffect(() => {
    if (show) {
      openModal(modalId);
    }
  }, [show, modalId, openModal]);

  useEffect(() => {
    if (show && activeModalId && activeModalId !== modalId) {
      handleClose();
    }
  }, [show, activeModalId, modalId, handleClose]);

  return useCallback(() => {
    closeModal(modalId);
    handleClose();
  }, [closeModal, handleClose, modalId]);
};
